import GraphQL from '@app/model/_common/service/GraphQL';
import { gql } from 'graphql-request';

export const REVIEW_FRAGMENT = gql`
    fragment ReviewFields on Review {
        id
        hotelId
        provider
        providerId
        creationDate
        roomName
        score
        reviewDate
        bookInfo {
            checkInDate
            checkOutDate
            nights
        }
        customerInfo {
            username
            name
            country
            customerType
        }
        texts {
            id
            creationDate
            type
            language
            text
            translation
            topics {
                id
                creationDate
                category {
                    name
                    id
                    parent {
                        id
                        name
                    }
                }
                topicFragment
                topicFragmentPosition {
                    to
                    from
                }
                sentiment
                impact
            }
        }
    }`;

export const REVIEWS_STATE_STATISTICS_FRAGMENT = gql`
    fragment ReviewsStateStatisticsFragment on ReviewsStateStatistics {
        numberOfReviews
        repliedCount
        positiveSentimentCount
        negativeSentimentCount
        neutralSentimentCount
        totalImpact
        score
        responseRate
        positivityRate
    }
`;

export const REVIEWS_STATE_FOR_PERIOD_FRAGMENT = gql`
    ${REVIEWS_STATE_STATISTICS_FRAGMENT}
    fragment ReviewsStateForPeriodFragment on ReviewsStateForPeriod {

        companyHotelId
        from
				to
        competitorReviewsStates {
            competitorId
            hotelId
            hotelName
            isCompetitor
            isMainHotel
            statistics {
                ...ReviewsStateStatisticsFragment
            }
            stateStatisticsForProviders {
                provider
                statistics {
                    ...ReviewsStateStatisticsFragment
                }
            }
            stateStatisticsForCategories {
                category {
                    id
                    name
                    isRoot
                    children {
                        id
                        name
                        isRoot
                    }
                }
                statistics {
                    ...ReviewsStateStatisticsFragment
                }
            }
        }

    }
`;

export const REVIEW_HANDLING_SESSION = gql`
    fragment ReviewHandlingSessionFields on ReviewHandlingSession {
        id
        creationDate
        assignedDepartmentId
        state
        reply {
            profileId
            text
            creationDate
        }
        logEntries {
            id
            type
            creationDate
            profileId
            profileName
            notes
        }
    }`;

export const REVIEW_OUTPUT_FRAGMENT = gql`
    ${REVIEW_FRAGMENT}
    ${REVIEW_HANDLING_SESSION}
    fragment ReviewOutputFields on ReviewOutput {
        hotelName
        review {
            ...ReviewFields
        }
        session {
            ...ReviewHandlingSessionFields
        }
    }
`;

export const FILTER_REVIEWS_QUERY: GraphQL = {
	name: 'filterReviews',
	gql: gql`
      ${REVIEW_OUTPUT_FRAGMENT}
      query filterReviews($filter:ReviewsFilterInput!) {
          filterReviews(filter: $filter) {
              ...ReviewOutputFields
          }
      }

	`,
};

export const FIND_REVIEWS_FOR_TEXT_CATEGORY_QUERY: GraphQL = {
	name: 'findReviewsForTextCategory',
	gql: gql`
      ${REVIEW_FRAGMENT}
      query findReviewsForTextCategory($companyHotelId: Long!, $hotelId: Long!, $categoryId: Long!) {
          findReviewsForTextCategory(companyHotelId: $companyHotelId, hotelId: $hotelId, categoryId: $categoryId) {
              ...ReviewFields
          }
      }

	`,
};

export const FIND_REVIEWS_STATE_FOR_PERIOD_QUERY: GraphQL = {
	name: 'findReviewsStateForPeriod',
	gql: gql`
      ${REVIEWS_STATE_FOR_PERIOD_FRAGMENT}
      query findReviewsStateForPeriod($companyHotelId: Long!, $filter: ReviewsStateFilter) {
          findReviewsStateForPeriod(companyHotelId: $companyHotelId, filter: $filter) {
              ...ReviewsStateForPeriodFragment
          }
      }

	`,
};

export const FIND_GROUP_REVIEWS_STATE_FOR_PERIOD_QUERY: GraphQL = {
	name: 'findGroupReviewsStateForPeriod',
	gql: gql`
      ${REVIEWS_STATE_FOR_PERIOD_FRAGMENT}
      query findGroupReviewsStateForPeriod($filter: ReviewsStateFilter) {
          findGroupReviewsStateForPeriod(filter: $filter) {
              monthPeriod
              reviewsStates {
                  ...ReviewsStateForPeriodFragment
              }
          }
      }

	`,
};

export const TRANSLATE_REVIEW_MUTATION: GraphQL = {
	name: 'translateReview',
	gql: gql`
      ${REVIEW_FRAGMENT}
      mutation translateReview($reviewId: Long!) {
          translateReview(reviewId: $reviewId) {
              ...ReviewFields
          }
      }

	`,
};

export const MARK_REVIEW_AS_VIEWED_BY_USER_MUTATION: GraphQL = {
	name: 'markReviewAsViewedByUser',
	gql: gql`
      ${REVIEW_HANDLING_SESSION}
      mutation markReviewAsViewedByUser($reviewId: Long!) {
          markReviewAsViewedByUser(reviewId: $reviewId) {
              ...ReviewHandlingSessionFields
          }
      }

	`,
};

export const SAVE_REVIEW_INTERNAL_NOTES_MUTATION: GraphQL = {
	name: 'saveReviewInternalComment',
	gql: gql`
      ${REVIEW_HANDLING_SESSION}
      mutation saveReviewInternalComment($input: ReviewInternalNotesInput!) {
          saveReviewInternalComment(input: $input) {
              ...ReviewHandlingSessionFields
          }
      }

	`,
};

export const REMOVE_REVIEW_INTERNAL_NOTES_MUTATION: GraphQL = {
	name: 'removeReviewInternalNotes',
	gql: gql`
      ${REVIEW_HANDLING_SESSION}
      mutation removeReviewInternalNotes($reviewId: Long!, $logId: Long!) {
          removeReviewInternalNotes(reviewId: $reviewId, logId: $logId) {
              ...ReviewHandlingSessionFields
          }
      }

	`,
};

export const MARK_REVIEW_AS_ANSWERED: GraphQL = {
	name: 'markReviewAsAnswered',
	gql: gql`
      ${REVIEW_HANDLING_SESSION}
      mutation markReviewAsAnswered($reviewId: Long!) {
          markReviewAsAnswered(reviewId: $reviewId) {
              ...ReviewHandlingSessionFields
          }
      }

	`,
};

export const MARK_REVIEW_AS_UNANSWERED: GraphQL = {
	name: 'markReviewAsUnanswered',
	gql: gql`
      ${REVIEW_HANDLING_SESSION}
      mutation markReviewAsUnanswered($reviewId: Long!) {
          markReviewAsUnanswered(reviewId: $reviewId) {
              ...ReviewHandlingSessionFields
          }
      }

	`,
};

export const FIND_REVIEW_CATEGORIES_QUERY: GraphQL = {
	name: 'findReviewCategories',
	gql: gql`
      query findReviewCategories {
          findReviewCategories {
              id
              name
              children {
                  id
                  name
                  isRoot
              }
          }
      }
	`,
};

export const REVIEWS_INSIGHT_FRAGMENT = gql`
    fragment ReviewsInsightFragment on ReviewsInsight {
        hotelId
        insight {
            title
            body
            reviewTextCategoryId
            reviewTextSubCategoryId
            sentiment
            lowConfidence
            signal {
                metric
                currentValue
                compareValue
                delta
            }
        }
    }
`;

export const FIND_REVIEWS_WEEKLY_DIGEST_QUERY: GraphQL = {
	name: 'findReviewsWeeklyDigest',
	gql: gql`
      ${REVIEWS_STATE_FOR_PERIOD_FRAGMENT}
      ${REVIEWS_INSIGHT_FRAGMENT}
      query findReviewsWeeklyDigest($companyHotelId: Long!) {
          findReviewsWeeklyDigest(companyHotelId: $companyHotelId) {
              current {
                  ...ReviewsStateForPeriodFragment
              }
              previous {
                  ...ReviewsStateForPeriodFragment
              }
              insights {
                  ...ReviewsInsightFragment
              }
          }
      }
	`,
};


export const FIND_REVIEWS_MONTHLY_DIGEST_QUERY: GraphQL = {
	name: 'findReviewsMonthlyDigest',
	gql: gql`
      ${REVIEWS_STATE_FOR_PERIOD_FRAGMENT}
      ${REVIEWS_INSIGHT_FRAGMENT}
      query findReviewsMonthlyDigest($companyHotelId: Long!) {
          findReviewsMonthlyDigest(companyHotelId: $companyHotelId) {
              current {
                  ...ReviewsStateForPeriodFragment
              }
              previous {
                  ...ReviewsStateForPeriodFragment
              }
              insights {
                  ...ReviewsInsightFragment
              }
          }
      }
	`,
};

export const REPORTS_DELIVERY_SCHEDULE_FRAGMENT = gql`
    fragment ReportsDeliveryScheduleFragment on ReportsDeliverySchedule {
        id
        companyHotelId
        creationDate
        state
        recipients
        dayOfMonth
        weekdays
        time
        scheduleType
        reportIds
        nextExecutionDate
        timezone
    }
`;

export const FIND_REVIEWS_INSIGHTS_DELIVERY_SCHEDULES_QUERY: GraphQL = {
	name: 'findReviewsInsightsDeliverySchedules',
	gql: gql`
      ${REPORTS_DELIVERY_SCHEDULE_FRAGMENT}
      query findReviewsInsightsDeliverySchedules($companyHotelId: Long!) {
          findReviewsInsightsDeliverySchedules(companyHotelId: $companyHotelId) {
              ...ReportsDeliveryScheduleFragment
          }
      }
	`,
};

export const SAVE_REVIEWS_INSIGHTS_DELIVERY_SCHEDULE_MUTATION: GraphQL = {
	name: 'saveReviewsInsightsDeliverySchedule',
	gql: gql`
      ${REPORTS_DELIVERY_SCHEDULE_FRAGMENT}
      mutation saveReviewsInsightsDeliverySchedule($input: ReviewsInsightsDeliveryScheduleInput!) {
          saveReviewsInsightsDeliverySchedule(input: $input) {
              ...ReportsDeliveryScheduleFragment
          }
      }
	`,
};
